import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileDropdown from '@/components/feature/ProfileDropdown';

interface Notification {
  id: string;
  title: string;
  description: string;
  time: string;
  read: boolean;
  type: 'info' | 'warning' | 'success' | 'error';
}

interface TopBarProps {
  pageTitle: string;
  pageSubtitle?: string;
  breadcrumbs?: { label: string; path?: string }[];
  notifications?: Notification[];
}

const typeIconMap = {
  info:    { icon: 'ri-information-line',   color: 'text-semantic-info' },
  warning: { icon: 'ri-alert-line',          color: 'text-semantic-warning' },
  success: { icon: 'ri-checkbox-circle-line', color: 'text-semantic-success' },
  error:   { icon: 'ri-error-warning-line',  color: 'text-semantic-error' },
};

const languages = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
];

export default function TopBar({ pageTitle, pageSubtitle, breadcrumbs, notifications = [] }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const [notifOpen, setNotifOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const unread = notifications.filter((n) => !n.read).length;

  return (
    <header className="h-16 bg-background-50 border-b border-background-200 px-4 md:px-6 flex items-center justify-between flex-shrink-0 relative z-20">
      {/* Left: title + breadcrumbs */}
      <div className="ml-10 lg:ml-0">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1.5 mb-0.5">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />}
                <span className={`text-xs font-label ${i === breadcrumbs.length - 1 ? 'text-foreground-700 font-medium' : 'text-foreground-400'}`}>
                  {crumb.label}
                </span>
              </span>
            ))}
          </nav>
        )}
        <h1 className="font-heading font-700 text-foreground-900 text-base md:text-lg leading-tight truncate max-w-[180px] sm:max-w-none">{pageTitle}</h1>
        {pageSubtitle && (
          <p className="hidden sm:block text-xs text-foreground-500 font-label mt-0.5">{pageSubtitle}</p>
        )}
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <button
          aria-label={t('common.search')}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer"
        >
          <i className="ri-search-line text-base" />
        </button>

        {/* Language Switcher */}
        <div className="relative">
          <button
            aria-label="Change language"
            onClick={() => setLangOpen(!langOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer relative"
          >
            <i className="ri-globe-line text-base" />
          </button>

          {langOpen && (
            <div className="absolute right-0 top-12 w-40 bg-background-50 rounded-xl shadow-floating border border-background-200 overflow-hidden z-30">
              <div className="px-3 py-2 border-b border-background-200">
                <p className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide">{t('common.language')}</p>
              </div>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-label text-left hover:bg-background-100 transition-colors cursor-pointer ${
                    i18n.language === lang.code ? 'text-primary-600 font-medium bg-primary-50/60' : 'text-foreground-700'
                  }`}
                >
                  <span className="text-xs font-bold uppercase text-foreground-400 w-6">{lang.code}</span>
                  <span>{lang.native}</span>
                  {i18n.language === lang.code && (
                    <i className="ri-check-line text-primary-500 text-sm ml-auto" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            aria-label={`${t('common.notifications')}${unread > 0 ? `, ${unread} unread` : ''}`}
            onClick={() => setNotifOpen(!notifOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer relative"
          >
            <i className="ri-notification-3-line text-base" />
            {unread > 0 && (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-[9px] font-label font-700">
                {unread}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-12 w-80 bg-background-50 rounded-xl shadow-floating border border-background-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-background-200 flex items-center justify-between">
                <h3 className="font-heading font-700 text-foreground-800 text-sm">{t('common.notifications')}</h3>
                {unread > 0 && (
                  <button className="text-xs text-accent-600 hover:text-accent-700 font-label font-medium cursor-pointer">
                    {t('common.markAllRead')}
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <i className="ri-notification-off-line text-2xl text-foreground-300" />
                    <p className="text-sm text-foreground-400 font-label mt-2">{t('common.noNotifications')}</p>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const cfg = typeIconMap[n.type];
                    return (
                      <div
                        key={n.id}
                        className={`px-4 py-3 flex items-start gap-3 border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer ${!n.read ? 'bg-primary-50/40' : ''}`}
                      >
                        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-background-100`}>
                          <i className={`${cfg.icon} text-sm ${cfg.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-800 font-label leading-tight">{n.title}</p>
                          <p className="text-xs text-foreground-500 font-label mt-0.5 leading-snug">{n.description}</p>
                          <p className="text-xs text-foreground-400 font-label mt-1">{n.time}</p>
                        </div>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Help */}
        <button
          aria-label={t('common.help')}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer"
        >
          <i className="ri-question-line text-base" />
        </button>

        {/* Profile */}
        <div className="ml-1 pl-1 border-l border-background-200">
          <ProfileDropdown />
        </div>
      </div>
    </header>
  );
}